
<template>
    <div>
        <div>A control for displaying a table of items with configurable columns. In order to specify this it is necessary to know the properties (fields) of the items being returned. 
            You may also need to know the name of the result set the items are returned within.
        </div> 
        <v-row>
            <v-col>
                <v-checkbox v-model="multiSelect" label="Multi-select" @change="emit"/>
                <span class="body-2 text--secondary">If enabled, allows multiple values to be selected.</span>
            </v-col>
            <v-col>
                <v-checkbox v-model="canReorderRows" label="Can Reorder Rows" @change="emit"/>
                <span class="body-2 text--secondary">If enabled, allows rows to be reordered by drag and drop.</span>
            </v-col>
            <v-col>
                <v-checkbox v-model="has3StateSort" label="Has 3 State Sort" @change="emit"/>
                <span class="body-2 text--secondary">If enabled, allows sort to include the unsorted state.</span>
            </v-col>
            <v-col>
                <v-checkbox v-model="canHide" label="Hide Column" @change="emit"/>
                <span class="body-2 text--secondary">If enabled, adds column to allow hiding of items.</span>
            </v-col>
            <v-col>
                <v-checkbox v-model="compact" label="Compact Style" @change="emit"/>
                <span class="body-2 text--secondary">If enabled displays table using a compact style.</span>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-text-field v-model="gridHeight" type="string" label="Grid Height" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The height of the grid in the control. Can be a number (assumed px) or string (any valid css).</span>
            </v-col>
            <v-col>
                <v-text-field v-model="resultSet" type="string" label="Result Set" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The name of the result set to use. Default uses the first set returned.</span>
            </v-col>
            <v-col>
                <v-text-field v-model="itemId" type="string" label="Item Id" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The name of the property to use as the items Id. Default is 'Id'</span>
            </v-col>
            <v-col>
                <v-text-field v-model="sortField" type="string" label="Sort Field" @input="emit" @change="emit"/>
                <span class="body-2 text--secondary">The field by which the items should be sorted.</span>
            </v-col>
            <v-col>
                <v-select :items="[{text: 'Ascending', value: 'asc'}, {text: 'Descending', value: 'desc'}]" v-model="sortDirection" label="Sort Direction" @change="emit"/>
                <span class="body-2 text--secondary">How to sort the items. Default is Ascending.</span>
            </v-col>         
        </v-row>
        <div class="columnsWrapper">
            <label class="v-label theme--dark">Column Definitions</label>
            <v-row class="entryRow">
                <v-col>
                    <v-text-field v-model="columnField" type="string" label="Field"/>
                    <span class="body-2 text--secondary">The field for this column.</span>
                </v-col>
                <v-col>
                    <v-text-field v-model="columnDisplay" type="string" label="Display name"/>
                    <span class="body-2 text--secondary">The display name for this column.</span>
                </v-col>
                <v-col>
                    <v-text-field v-model="columnWidth" type="string" label="Width"/>
                    <span class="body-2 text--secondary">The width in pixels for this column.</span>
                </v-col>
                <v-col>
                    <v-btn dark @click="save">Save Column Definition</v-btn>
                </v-col>
            </v-row>

            <v-row class="headerRow">
                <div class="columnField">Field</div>
                <div class="columnField">Display Name</div>
                <div class="columnField">Width (optional)</div>
                <div class="columnField">Delete Column Definition</div>
            </v-row>
            <div class="columnDefs">
                <v-row v-for="(col, idx) in columns" v-bind:key="idx" @click="selectColumn(col)" :class="selectedIdx === idx ? 'selected' : null">
                    <div class="columnField text--secondary">
                        {{col.field}}
                    </div>
                    <div class="columnField text--secondary">
                        {{col.displayName}}
                    </div>
                    <div class="columnField text--secondary">
                        {{col.width}}
                    </div>
                    <div class="columnField text--secondary">
                        <v-icon @click.stop="remove(col)" color="red">mdi-close</v-icon>
                    </div>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'LoggerTable',
        data: () => ({
            multiSelect: false,
            canReorderRows: false,
            has3StateSort: false,
            canHide: false,
            compact: false,
            gridHeight: null,
            resultSet: null,
            itemId: null,
            sortField: null,
            sortDirection: 'asc',
            columnField: null,
            columnDisplay: null,
            columnWidth: null,
            columns: [],
            selectedIdx: 0
        }),
        computed: {
        },
        methods: {
            emit() {
                let opts = {
                    type: 'loggertable', 
                    multiSelect: this.multiSelect,
                    canReorderRows: this.canReorderRows,
                    has3StateSort: this.has3StateSort,
                    canHide: this.canHide,
                    compact: this.compact
                    };
                if (this.gridHeight){
                    let reg = new RegExp('^\\d*$');
                    if (reg.test(this.gridHeight)){
                        // Just numeric
                        opts.gridHeight = parseInt(this.gridHeight);
                    } else {
                        // Just treat as a string - no attempt to check validity
                        opts.gridHeight = this.gridHeight;
                    }
                }

                if (this.resultSet){
                    opts.resultSet = this.resultSet;
                }

                if (this.itemId){
                    opts.itemId = this.itemId;
                }

                if (this.sortField){
                    let sort = {field: this.sortField, direction: this.sortDirection};
                    opts.sortOptions = sort;
                }

                if (this.columns.length > 0){
                    let colDefs = [];                    
                    this.columns.forEach(col => {
                        if (col.field != null){
                            let entry = {field: col.field, displayName: col.displayName};
                            if (col.width){
                                entry.width = col.width;
                            }
                            colDefs.push(entry);
                        }
                    });
                    opts.columns = colDefs;
                }

                this.$emit('change', opts);
            },
            addNew(){
                // Clear the data and increase selected row index
                this.selectedIdx = this.columns.length;
                this.columnField = null;
                this.columnDisplay = null;
                this.columnWidth = null;
            },
            save(){
                // Save the data to the current row
                if (this.columnField != null){
                    this.columns[this.selectedIdx] = {field: this.columnField, displayName: this.columnDisplay, width: this.columnWidth};
                    this.addNew();
                    this.emit();                
                }
            },
            selectColumn(col){
                let idx = this.columns.findIndex(c => c.field === col.field);
                this.selectedIdx = idx;
                this.columnField = col.field;
                this.columnDisplay = col.displayName;
                this.columnWidth = col.width;
            },
            remove(col){
                let idx = this.columns.findIndex(c => c.field === col.field);
                this.columns.splice(idx, 1);
                this.emit();
            }
        },
        created() {
            this.emit();
        }
    }
</script>

<style lang="scss" scoped>
    .columnsWrapper {
    
        .selected {
            border: 1px solid rgba($color: white, $alpha: 0.5);
        }
        
        .columnField {
            margin: 0 15px;
            display: flex;
            flex: 1 1 0;
        }
    }
</style>